import React, { ReactElement } from 'react';
import PropTypes from 'prop-types';
import type { Validator } from 'prop-types';
import uniq from 'lodash/fp/uniq';
import difference from 'lodash/fp/difference';
import { useTheme } from '../theming';

import { ShortText } from '../Typography';
import { Spinner } from '../Spinner';
import { responsivePropType } from '../utils/responsiveProp';
import { combinePropTypes } from '../utils/combinePropTypes';
import { PolymorphicRef } from '../utils/types';
import type { InternalIconProps } from '../Icon/GenericIcon';
import type { ButtonProps, ButtonComponent, ButtonColorLegacy } from './types';
import {
  getColorWithLegacyValuesMapped,
  getColorTokens,
  getSizeWithLegacyValuesMapped,
  getFontSize,
  getIconSize,
  getSpinnerSize,
} from './utils';
import {
  StyledButton,
  StyledLinkButton,
  SpinnerContainer,
  StyledIconWrapper,
} from './Button.styles';
import { buttonSizeLegacy, buttonWithTextSize, buttonWithoutTextSize } from './sizes';

export const NO_CHILDREN_NO_ICON_ERROR =
  'Either `children` or `icon` prop is required for a Button component. If this is intended, you can, however, use the BaseButton for this purpose.';

export const NO_CHILDREN_NO_TITLE_ERROR =
  'When creating a button with only an icon, make sure to pass a title prop. It will be used as aria-label.';

function _Button<C extends 'a' | 'button' = 'button'>(
  props: ButtonProps<C>,
  ref?: PolymorphicRef<C>
) {
  const {
    children,
    icon,
    alignIcon = 'start',
    size = 'default',
    // `primary` is a deprecated value, but we keep it as the default for now to make the rebranding as smooth
    // as possible, so that orange buttons in clients' projects are still orange.
    // TODO: change default to `primary-default` when we remove the deprecated color values.
    color = 'primary',
    fullWidth = false,
    minWidth,
    title,
    loading,
    disabled,
    disableHoverStyle = false,
    ...otherProps
  } = props;

  // `color` and `size` support some deprecated legacy values.
  // Here we map any legacy values to their new equivalents so that we know that we have only new values from here on.
  // TODO: remove this mapping and use `color` and `size` directly once all clients finished migrating to the new values or we created a codemod.
  const preparedColor = getColorWithLegacyValuesMapped(color);
  const preparedSize = getSizeWithLegacyValuesMapped(size);

  const hasIconOnly = !!icon && !children;
  const commonProps = {
    title,
    icon,
    size: preparedSize,
    color: preparedColor,
    disableHoverStyle,
    minWidth,
    disabled,
    ref,
    // Turn this into a transient prop for styled-components. Otherwise, it throws a React error in dev mode.
    // @see https://styled-components.com/docs/faqs#why-am-i-getting-html-attribute-warnings
    $loading: loading,
    hasIconOnly,
    alignIcon,
    fullWidth,
    'aria-label': title,
    ...otherProps,
  };

  let iconContent: ReactElement<InternalIconProps> | undefined = icon;
  let Icon;
  const currentTheme = useTheme();
  const spinnerColor = getColorTokens(preparedColor, currentTheme);
  const fontSize = getFontSize(preparedSize);
  const iconSize = getIconSize(preparedSize);
  const spinnerSize = getSpinnerSize(preparedSize);

  if (iconContent?.props.color !== undefined) {
    console.warn(
      'Please remove `color` property from <Icon /> in <Button /> component. This property will be ignored, as we want to make sure that icon and text color in the button always match'
    );
  }

  if (iconContent) {
    // Color property in icon will be ignored, as we want to make sure that icon and text color in the button always match.
    iconContent = React.cloneElement(iconContent, { color: 'currentColor', size: iconSize });
    Icon = <StyledIconWrapper>{iconContent}</StyledIconWrapper>;
  }

  const commonContent = (
    <>
      {loading ? (
        <SpinnerContainer spinnerSize={spinnerSize}>
          <Spinner size={spinnerSize} color={spinnerColor.default.foreground} strokeWidth={2} />
        </SpinnerContainer>
      ) : (
        <>
          {alignIcon === 'start' && Icon}

          {children && (
            <ShortText
              weight={'strong'}
              wordBreak={false}
              as={'span'}
              color={'currentColor'}
              size={fontSize}
            >
              {children}
            </ShortText>
          )}

          {alignIcon === 'end' && Icon}
        </>
      )}
    </>
  );

  if (props.as === 'a') {
    return <StyledLinkButton {...commonProps}>{commonContent}</StyledLinkButton>;
  }

  return <StyledButton {...commonProps}>{commonContent}</StyledButton>;
}

export const Button = React.forwardRef(_Button) as ButtonComponent;

const checkOptionalProps: Validator<string | React.ReactElement> = (props) => {
  const p = props as ButtonProps;

  if (!p.children && !p.icon) {
    return new Error(NO_CHILDREN_NO_ICON_ERROR);
  }

  if (!p.children && !p.title) {
    return new Error(NO_CHILDREN_NO_TITLE_ERROR);
  }

  return null;
};

const checkValidSizeUsage: Validator<ButtonProps['size']> = (props) => {
  const p = props as ButtonProps;
  if (p.children && p.size) {
    const invalidSizeForButtonWithText: string[] = difference(
      buttonWithoutTextSize,
      buttonWithTextSize
    );
    const isValidSize = (size: string) => !invalidSizeForButtonWithText.includes(size);
    const hasValidSizeValue =
      typeof p.size === 'string'
        ? isValidSize(p.size)
        : Object.values(p.size).every((size) => !size || isValidSize(size));

    if (!hasValidSizeValue) {
      return new Error(
        `When creating a \`Button\` with text, the sizes ${invalidSizeForButtonWithText
          .map((value) => `\`${value}\``)
          .join(', ')} are not supported.`
      );
    }
  }

  return null;
};

// We keep the legacy color values for now to avoid too many breaking changes in v5 of the package. We'll likely
// create a codemod later so that clients can migrate with ease.
// TODO: remove once all clients finished migrating to the new values or we created a codemod.
const buttonColorLegacyValues: ButtonColorLegacy[] = [
  'primary',
  'secondary',
  'tertiary',
  'inverse',
];

Button.propTypes = {
  // Children is considered never here because later we say the size is any ButtonSize and some of those sizes have children as never in the types
  // Given we can't have propTypes match both sides of the type union, let's pretend this is a null validator to satisfy typescript
  children: PropTypes.node as Validator<null>,
  // Same as above, here string is considered to be mandatory, which isn't true but we can let typescript have it
  title: checkOptionalProps as Validator<string>,
  icon: checkOptionalProps as Validator<
    React.ReactElement<Omit<InternalIconProps, 'color' | 'size'>>
  >,
  alignIcon: PropTypes.oneOf(['start', 'end'] as const),
  size: combinePropTypes(
    responsivePropType(
      PropTypes.oneOf(uniq([...buttonSizeLegacy, ...buttonWithoutTextSize, ...buttonWithTextSize]))
    ),
    checkValidSizeUsage
  ),
  color: PropTypes.oneOf([
    ...buttonColorLegacyValues,
    'primary-default',
    'secondary-default',
    'primary-emphasis',
    'secondary-emphasis',
    'ghost',
    'primary-inverse',
    'secondary-inverse',
    'ghost-inverse',
  ] as const),
  disabled: PropTypes.bool,
  fullWidth: responsivePropType(PropTypes.bool),
  loading: PropTypes.bool,
  minWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  disableHoverStyle: PropTypes.bool,
};
